import { initializeClassSelection } from '../host/class-selection';
import { initializeMissionSelection } from '../host/mission-selection';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { setReader } from '../messages';
import { callCore, isUnauthenticated } from './rpc';

/**
 * Les trois lectures dont la coque a besoin, et rien de plus : qui je suis, quelles apps mon
 * enseignant a ouvertes, et quel espace de travail m'a été attribué.
 *
 * `retry: false` partout : une session expirée (401) ne doit pas être retentée trois fois avant
 * de ramener à l'écran de connexion, et une erreur de portée (« vous enseignez dans plusieurs
 * classes ») ne devient jamais vraie en réessayant.
 */

export interface MeResult {
  id: string;
  role: 'student' | 'teacher' | 'admin';
  isAdmin: boolean;
  displayName: string;
  mustChangePassword: boolean;
  classes: ReadonlyArray<{ id: string; name: string }>;
}

export interface DeskAppsResult {
  appIds: readonly string[];
}

export interface WorkspaceResult {
  workspaceId: string;
  classId: string;
  companyId: string;
  /**
   * `'student'` — l'espace d'un élève ; `'demo'` — l'espace de DÉMONSTRATION d'un enseignant
   * (`workspace.demo`, chantier 9). Le bureau s'en sert pour dire en permanence à l'adulte qu'il
   * est en démonstration : c'est projeté devant une classe, et la confusion coûterait cher.
   */
  kind: 'student' | 'demo' | 'group';
  missionRunId?: string;
  readOnly?: boolean;
  /**
   * `core.workspaces.generation` — le compteur de remises à zéro. `AppHost` l'inclut dans la clé de
   * montage de l'app : un élève dont l'enseignant vient de remettre le fichier à zéro voit son
   * outil repartir du fichier neuf, sans avoir à recharger la page.
   */
  generation: number;
}

export const meQueryKey = ['core', 'me'] as const;

/**
 * Qui je suis — et, du même coup, **pour qui la coque écrit**.
 *
 * `setReader` est appelée ici et nulle part ailleurs (hors déconnexion) : le rôle du lecteur est
 * une propriété de la session, elle est donc posée là où la session arrive. C'est une fonction de
 * requête, donc du code hors rendu — jamais un effet de bord pendant qu'un composant se peint — et
 * elle s'exécute avant que `DeskLayout` ne laisse monter le moindre écran du bureau (`App.tsx`).
 * Voir `src/messages.ts` pour la règle complète.
 */
export function useMe(): UseQueryResult<MeResult, unknown> {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: async () => {
      const me = await callCore<MeResult>('me');
      setReader(me.role);
      initializeMissionSelection(me.id, me.role);
      initializeClassSelection(me.id, me.classes);
      return me;
    },
    retry: false,
    staleTime: 30_000,
  });
}

/** `classId` n'est passé que lorsqu'il est réellement choisi : `desk.apps` résout seul la classe
 * d'un élève (il n'en a qu'une) et refuse de deviner pour un enseignant multi-classe — c'est
 * pourquoi la barre de menu propose un vrai sélecteur dans ce cas. */
export function useDeskApps(classId: string | undefined): UseQueryResult<DeskAppsResult, unknown> {
  return useQuery({
    queryKey: ['core', 'desk.apps', classId ?? null],
    queryFn: () => callCore<DeskAppsResult>('desk.apps', classId ? { classId } : {}),
    retry: false,
    staleTime: 15_000,
  });
}

/** `null` veut dire « rien à charger pour l'instant » (aucun espace provisionné), jamais une
 * erreur — c'est le contrat de `desk.resolveWorkspace` (`packages/core-domain`). */
export function useWorkspace(
  classId: string | undefined,
  missionRunId?: string,
): UseQueryResult<WorkspaceResult | null, unknown> {
  return useQuery({
    queryKey: ['core', 'desk.resolveWorkspace', classId ?? null, missionRunId ?? null],
    queryFn: async () => {
      const workspace = await callCore<WorkspaceResult | null>('desk.resolveWorkspace', {
        ...(classId ? { classId } : {}),
        ...(missionRunId ? { missionRunId } : {}),
      });
      // Une ancienne API qui ignore le choix de séance ne doit jamais ouvrir le fichier par défaut.
      if (missionRunId && workspace?.missionRunId !== missionRunId)
        throw new Error('Le serveur ne prend pas encore en charge le fichier de mission choisi.');
      return workspace;
    },
    retry: false,
    staleTime: 15_000,
  });
}

/* --- Espace enseignant -----------------------------------------------------------------------
   Quatre lectures, toutes réservées au rôle enseignant CÔTÉ SERVEUR (`role: 'teacher'` sur chaque
   opération, `packages/core-domain/src/operations/*`) : un élève qui appellerait ces adresses à la
   main reçoit `ROLE_FORBIDDEN` (403) avant le moindre SQL. `enabled` ci-dessous ne protège rien —
   il évite seulement d'envoyer une requête dont on sait déjà qu'elle n'a pas de sens (pas encore
   de classe choisie). */

export interface TeacherClass {
  id: string;
  name: string;
  isSandbox: boolean;
  /** L'année scolaire de la classe. Le sélecteur de classe REGROUPE par année plutôt que
   * d'empiler : en septembre prochain, la 1A nouvelle et la 1A de cette année portent le même
   * nom, et seule l'année les distingue. Aucune classe n'est jamais masquée pour autant — voir
   * `packages/core-domain/src/operations/school-years.ts`. */
  schoolYearId: string;
  schoolYearLabel: string;
  isCurrentYear: boolean;
}

export interface TeacherPresenceStudent {
  userId: string;
  displayName: string;
  status: 'active' | 'inactive' | 'never_connected';
  appId: string | null;
  route: string | null;
  lastSeenAt: string | null;
  /** Le dernier geste de cet élève dans cette classe — ce que le cahier des charges (§21) demande
   * en plus de la présence. Même source que le journal (`journal.list`) : la dernière ligne de
   * `core.events` qui porte un libellé lisible. `null` tant que l'élève n'a rien écrit. */
  lastAction: { label: string; subject: string | null; at: string } | null;
}

export interface ClassStudent {
  userId: string;
  displayName: string;
  studentCode: string | null;
  /** L'identifiant du groupe, à côté de son nom : le sélecteur de groupe de « Ma classe » désigne
   * une LIGNE, jamais un nom — deux groupes renommés pendant que l'écran est ouvert lui feraient
   * sinon viser le mauvais. */
  groupId: string | null;
  groupName: string | null;
  mustChangePassword: boolean;
  isTest: boolean;
  disabled: boolean;
  lastLoginAt: string | null;
}

export interface StudentSpaceResult {
  student: {
    userId: string;
    displayName: string;
    studentCode: string | null;
    groupName: string | null;
    mustChangePassword: boolean;
    lastLoginAt: string | null;
  };
  presence: {
    status: 'active' | 'inactive' | 'never_connected';
    appId: string | null;
    route: string | null;
    lastSeenAt: string | null;
  };
  enabledAppIds: readonly string[];
  workspaces: ReadonlyArray<{
    workspaceId: string;
    companyName: string;
    status: string;
    generation: number;
    /** Celui que les outils de cet élève ouvrent réellement — voir `teacher.studentSpace`
     * (`@ndrc/core-domain`) : la liste sans ce champ laissait croire que les deux fichiers d'un
     * élève étaient également atteignables. */
    isCurrent: boolean;
    counts: ReadonlyArray<{ entity: string; count: number }>;
  }>;
}

/**
 * Le fichier client sur lequel une classe travaille (`classes.file`) — ce que ses élèves ouvrent
 * dans leurs trois outils, et combien d'entre eux l'ont réellement.
 */
export interface ClassFileResult {
  classId: string;
  companyId: string | null;
  companyName: string | null;
  studentsTotal: number;
  studentsWithSpace: number;
}

/** Les classes que CET enseignant enseigne — jamais celles d'un collègue (`classes.list` filtre sur
 * `ndrc.my_teacher_classes()`, et non sur l'existence de la classe). Distincte de `me.classes`,
 * qui liste les adhésions de l'acteur : c'est la même chose pour un enseignant aujourd'hui, mais
 * `classes.list` est la lecture dont le contrat PORTE cette garantie. */
export const teacherClassesQueryKey = ['core', 'classes.list'] as const;

export function useTeacherClasses(): UseQueryResult<{ classes: TeacherClass[] }, unknown> {
  return useQuery({
    queryKey: teacherClassesQueryKey,
    queryFn: () => callCore<{ classes: TeacherClass[] }>('classes.list'),
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * Le suivi de séance. `refetchInterval` : c'est l'écran que l'enseignant laisse ouvert sur son
 * second écran pendant deux heures — s'il fallait recharger la page pour le voir bouger, il ne
 * servirait à rien. 15 secondes : la moitié du battement de présence du bureau
 * (`PRESENCE_HEARTBEAT`), assez court pour que « qui vient d'arriver » se voie, assez long pour ne
 * pas marteler la base pendant toute la séance.
 */
export function useTeacherPresence(
  classId: string | undefined,
): UseQueryResult<{ students: TeacherPresenceStudent[] }, unknown> {
  return useQuery({
    queryKey: ['core', 'teacher.presence', classId ?? null],
    queryFn: () =>
      callCore<{ students: TeacherPresenceStudent[] }>('teacher.presence', {
        classId: classId ?? '',
      }),
    enabled: classId !== undefined,
    retry: false,
    refetchInterval: 15_000,
  });
}

/* --- Tenir la liste dans la durée --------------------------------------------------------------
   Les groupes d'une classe, les années scolaires de l'établissement, les collègues. Trois lectures
   réservées au rôle enseignant CÔTÉ SERVEUR (`role: 'teacher'` sur chaque opération) : `enabled`
   ci-dessous ne protège rien, il évite seulement une requête dont on sait déjà qu'elle n'a pas de
   sens. */

export interface ClassGroup {
  id: string;
  name: string;
  studentCount: number;
}

export interface SchoolYear {
  id: string;
  label: string;
  isCurrent: boolean;
  classCount: number;
}

export interface SchoolTeacher {
  userId: string;
  displayName: string;
  email: string | null;
  isFirst: boolean;
  isSelf: boolean;
  disabled: boolean;
  classes: ReadonlyArray<{ id: string; name: string }>;
}

export interface SchoolTeachersResult {
  /** Décidé par le SERVEUR, jamais par l'écran : seul le premier enseignant de l'établissement
   * crée des comptes de collègues. Un écran qui trancherait lui-même finirait par proposer un
   * bouton qui refuse — ou par cacher un bouton qui aurait marché. */
  canManage: boolean;
  teachers: SchoolTeacher[];
  manageableClasses: ReadonlyArray<{ id: string; name: string }>;
}

export const classGroupsQueryKey = ['core', 'groups.list'] as const;
export const schoolYearsQueryKey = ['core', 'schoolYears.list'] as const;
export const schoolTeachersQueryKey = ['core', 'teachers.list'] as const;

export function useClassGroups(
  classId: string | undefined,
): UseQueryResult<{ groups: ClassGroup[] }, unknown> {
  return useQuery({
    queryKey: [...classGroupsQueryKey, classId ?? null],
    queryFn: () => callCore<{ groups: ClassGroup[] }>('groups.list', { classId: classId ?? '' }),
    enabled: classId !== undefined,
    retry: false,
    staleTime: 10_000,
  });
}

export function useSchoolYears(): UseQueryResult<{ years: SchoolYear[] }, unknown> {
  return useQuery({
    queryKey: schoolYearsQueryKey,
    queryFn: () => callCore<{ years: SchoolYear[] }>('schoolYears.list'),
    retry: false,
    staleTime: 30_000,
  });
}

export function useSchoolTeachers(): UseQueryResult<SchoolTeachersResult, unknown> {
  return useQuery({
    queryKey: schoolTeachersQueryKey,
    queryFn: () => callCore<SchoolTeachersResult>('teachers.list'),
    retry: false,
    staleTime: 30_000,
  });
}

export function useClassStudents(
  classId: string | undefined,
): UseQueryResult<{ students: ClassStudent[] }, unknown> {
  return useQuery({
    queryKey: ['core', 'classes.students', classId ?? null],
    queryFn: () =>
      callCore<{ students: ClassStudent[] }>('classes.students', { classId: classId ?? '' }),
    enabled: classId !== undefined,
    retry: false,
    staleTime: 10_000,
  });
}

/** La clé de cache du fichier de la classe, exportée : distribuer un jeu de données ou lancer une
 * activité qui nomme une entreprise la CHANGE — les deux écrans doivent donc l'invalider, sans
 * quoi l'enseignant lirait encore le fichier d'avant son propre geste. */
export const classFileQueryKey = ['core', 'classes.file'] as const;

export function useClassFile(
  classId: string | undefined,
): UseQueryResult<ClassFileResult, unknown> {
  return useQuery({
    queryKey: [...classFileQueryKey, classId ?? null],
    queryFn: () => callCore<ClassFileResult>('classes.file', { classId: classId ?? '' }),
    enabled: classId !== undefined,
    retry: false,
    staleTime: 5_000,
  });
}

/**
 * L'espace que les outils d'un élève ouvrent RÉELLEMENT, résolu pour l'enseignant de sa classe
 * (`teacher.studentWorkspace`) — le jumeau de `desk.resolveWorkspace` quand c'est le travail de
 * quelqu'un d'autre qu'on lit.
 *
 * Même forme que `WorkspaceResult`, plus l'identité de l'élève : le bureau la sert au même endroit
 * (`useDesk().workspace`), et `AppHost` n'a donc pas deux chemins de montage. Le nom est là parce
 * que le bandeau doit dire de qui il s'agit — trente écrans se ressemblent.
 *
 * `refetchInterval` : l'élève continue de travailler pendant que son enseignant le lit. Une remise
 * à zéro, une redistribution, et `generation`/`workspaceId` changent — `AppHost` remonte alors
 * l'outil sur le bon espace, sans que l'enseignant ait à recharger sa page. Même cadence que le
 * suivi de séance.
 */
export interface StudentWorkspaceResult extends WorkspaceResult {
  companyName: string;
  student: { userId: string; displayName: string };
}

export function useStudentWorkspace(
  target: { classId: string; userId: string } | null,
): UseQueryResult<StudentWorkspaceResult, unknown> {
  return useQuery({
    queryKey: ['core', 'teacher.studentWorkspace', target?.classId ?? null, target?.userId ?? null],
    queryFn: () =>
      callCore<StudentWorkspaceResult>('teacher.studentWorkspace', {
        classId: target?.classId ?? '',
        userId: target?.userId ?? '',
      }),
    enabled: target !== null,
    retry: false,
    refetchInterval: 15_000,
  });
}

export function useStudentSpace(
  classId: string | undefined,
  userId: string | undefined,
): UseQueryResult<StudentSpaceResult, unknown> {
  return useQuery({
    queryKey: ['core', 'teacher.studentSpace', classId ?? null, userId ?? null],
    queryFn: () =>
      callCore<StudentSpaceResult>('teacher.studentSpace', {
        classId: classId ?? '',
        userId: userId ?? '',
      }),
    enabled: classId !== undefined && userId !== undefined,
    retry: false,
    refetchInterval: 15_000,
  });
}

/* --- L'activité ------------------------------------------------------------------------------
   La consigne de la séance, telle que l'élève la lit et telle que l'enseignant la suit.

   Les formes ci-dessous sont **redéclarées ici**, jamais importées de `@ndrc/core-domain` : le
   bureau ne dépend d'aucun paquet `@ndrc/core-*` (il n'appelle que des adresses HTTP), et c'est
   déjà la règle que suivent `MeResult`, `DeskAppsResult` et les quatre lectures enseignant
   ci-dessus. */

/**
 * Un document déposé par l'enseignant pour sa classe, tel que le cœur le rend — nom, poids, et
 * l'adresse signée par laquelle CETTE session peut l'ouvrir.
 *
 * `url` peut valoir `null` : le cœur ne fabrique une adresse que lorsque la requête vient d'une
 * vraie session HTTP (voir `CoreOperationContext.signFileUrl`, `@ndrc/core-domain`). L'écran
 * affiche alors le document sans lien plutôt qu'un lien mort — cas qui ne se produit pas dans un
 * navigateur, et qu'on refuse quand même d'inventer.
 */
export interface ClassDocument {
  id: string;
  name: string;
  mime: string;
  sizeBytes: number;
  createdAt: string;
  url: string | null;
}

export interface CurrentActivity {
  paused?: boolean;
  id: string;
  title: string;
  instructionsMd: string | null;
  /** Le document joint à la consigne, ou `null`. */
  document: ClassDocument | null;
  /** L'outil que la consigne fait ouvrir, ou `null`. Identifiant **opaque** pour la coque : elle
   * le rapproche du registre généré (`findManifest`) pour en afficher le nom et l'icône, et ne le
   * compare jamais à une valeur écrite en dur. */
  appId: string | null;
  startedAt: string;
  endsAt: string | null;
  /** Quand CET élève a déclaré avoir terminé — jamais quelqu'un d'autre : `activities.current`
   * borne la lecture à l'acteur. */
  completedAt: string | null;
}

export interface TeacherActivity {
  id: string;
  title: string;
  instructionsMd: string | null;
  status: string;
  companyId: string | null;
  resourceId: string | null;
  /** Le document joint, avec de quoi l'ouvrir — `null` s'il n'y en a pas ou s'il a été retiré. */
  document: ClassDocument | null;
  appId: string | null;
  endsAt: string | null;
  endedAt: string | null;
  createdAt: string;
  updatedAt: string;
  startsAt: string | null;
}

export interface ActivityProgressStudent {
  userId: string;
  displayName: string;
  completedAt: string | null;
}

export interface CheckFinding {
  ruleId: string;
  source: 'deterministic' | 'judgment';
  severity: 'error' | 'warning';
  subjectId: string;
  subjectLabel: string;
  field: string;
  message: string;
}

export interface ActivityCheckResult {
  workspaceId: string;
  scannedContacts: number;
  findings: CheckFinding[];
  /** Ce qui a été vérifié, en français. Vient du SERVEUR, dérivé des règles elles-mêmes — jamais
   * une liste recopiée dans `fr.json`, qui finirait par mentir sur ce que le code fait. */
  verified: string[];
  /** Ce qui ne l'est pas encore et demande un jugement. Affiché à côté de chaque réponse : c'est
   * ce qui empêche l'écran de dire « tout est correct ». */
  notVerified: string[];
}

/**
 * Ce que la relecture PAR JUGEMENT renvoie (`contacts.review`, `@ndrc/core-domain`). Redéclaré ici
 * plutôt qu'importé : le bureau ne dépend d'aucun paquet `@ndrc/core-*` — il parle au cœur par HTTP.
 *
 * `status: 'unavailable'` n'est **pas** une erreur : c'est un refus attendu (aucune clé sur cette
 * plateforme, plafond du jour atteint, usage coupé par l'enseignant, fournisseur en panne) qui
 * porte sa propre phrase française. L'écran l'affiche à côté des constats automatiques, qui, eux,
 * continuent de fonctionner — jamais à leur place.
 */
export type ContactsReviewResult =
  | {
      status: 'ok';
      classId: string;
      workspaceId: string;
      scannedContacts: number;
      findings: CheckFinding[];
      /** Ce qui a été relu une fois le jugement passé : les règles ET les points de jugement.
       * Avec `notVerified`, c'est la partition COMPLÈTE, déjà cohérente — le bureau la choisit,
       * il ne l'assemble pas (voir `lib/reviewScope.ts`). */
      verified: string[];
      /** Ce qui n'a été relu ni par les règles ni par le jugement. Disjoint de `verified`. */
      notVerified: string[];
      discardedRemarks: number;
      usage: {
        provider: string;
        model: string;
        inputTokens: number;
        outputTokens: number;
        costCents: number;
        latencyMs: number;
      };
      budget: { capCents: number; spentCents: number; remainingCents: number };
    }
  | {
      status: 'unavailable';
      classId: string;
      workspaceId: string;
      reason: 'not_configured' | 'policy_disabled' | 'budget_exhausted' | 'provider_error';
      message: string;
      budget: { capCents: number; spentCents: number; remainingCents: number } | null;
    };

export const currentActivityQueryKey = ['core', 'activities.current'] as const;

/**
 * La consigne en cours. `refetchInterval` : l'élève laisse son bureau ouvert pendant deux heures —
 * une consigne lancée en cours de séance doit apparaître sans qu'il ait à recharger la page.
 * Vingt secondes, du même ordre que le suivi de l'enseignant (quinze), assez espacé pour ne pas
 * marteler la base avec un élève par poste.
 */
export function useCurrentActivity(
  classId: string | undefined,
): UseQueryResult<{ activity: CurrentActivity | null }, unknown> {
  return useQuery({
    queryKey: [...currentActivityQueryKey, classId ?? null],
    queryFn: () =>
      callCore<{ activity: CurrentActivity | null }>(
        'activities.current',
        classId ? { classId } : {},
      ),
    retry: false,
    refetchInterval: 20_000,
  });
}

/**
 * La vérification du fichier client. `enabled: false` : elle ne part **jamais toute seule** —
 * c'est un geste de l'élève (« Vérifier mon travail »), déclenché par `refetch()`. Un contrôle qui
 * s'exécuterait en boucle derrière son dos lui dirait qu'il a tort avant même qu'il ait commencé.
 *
 * `null` veut dire « aucun espace de travail à vérifier pour l'instant », jamais une erreur —
 * c'est le contrat de l'opération, le même que `desk.resolveWorkspace`.
 */
export function useActivityCheck(
  classId: string | undefined,
  /** L'élève dont l'enseignant lit le travail, ou `null`. `activities.check` honore `userId` pour
   * un enseignant SEULEMENT et revérifie tout côté serveur (`resolveObservedWorkspace`) : un
   * élève qui poserait ce champ vérifierait toujours son propre fichier. */
  observing: { classId: string; userId: string } | null = null,
): UseQueryResult<ActivityCheckResult | null, unknown> {
  const scope =
    observing !== null
      ? { classId: observing.classId, userId: observing.userId }
      : classId
        ? { classId }
        : {};
  return useQuery({
    queryKey: ['core', 'activities.check', classId ?? null, observing?.userId ?? null],
    queryFn: () => callCore<ActivityCheckResult | null>('activities.check', scope),
    enabled: false,
    retry: false,
    gcTime: 0,
  });
}

export function useTeacherActivities(
  classId: string | undefined,
): UseQueryResult<{ activities: TeacherActivity[] }, unknown> {
  return useQuery({
    queryKey: ['core', 'activities.list', classId ?? null],
    queryFn: () =>
      callCore<{ activities: TeacherActivity[] }>('activities.list', { classId: classId ?? '' }),
    enabled: classId !== undefined,
    retry: false,
    staleTime: 5_000,
  });
}

/** Qui a terminé. Même cadence que `teacher.presence` : c'est le second écran que l'enseignant
 * laisse ouvert pendant la séance, il doit bouger tout seul. */
export function useActivityProgress(
  classId: string | undefined,
  activityId: string | undefined,
): UseQueryResult<{ students: ActivityProgressStudent[] }, unknown> {
  return useQuery({
    queryKey: ['core', 'activities.progress', classId ?? null, activityId ?? null],
    queryFn: () =>
      callCore<{ students: ActivityProgressStudent[] }>('activities.progress', {
        classId: classId ?? '',
        activityId: activityId ?? '',
      }),
    enabled: classId !== undefined && activityId !== undefined,
    retry: false,
    refetchInterval: 15_000,
  });
}

/* --- Rédaction assistée ----------------------------------------------------------------------
   Ce que les deux opérations d'IA de l'espace enseignant renvoient (`activities.suggest` et
   `datasets.generate`, `@ndrc/core-domain`). Redéclaré ici plutôt qu'importé, comme
   `ContactsReviewResult` ci-dessus : le bureau ne dépend d'aucun paquet `@ndrc/core-*`, il parle
   au cœur par HTTP.

   `status: 'unavailable'` n'est pas une erreur — c'est un refus attendu (aucune clé, plafond du
   jour atteint, usage coupé, fournisseur en panne) qui porte sa propre phrase française.
   `status: 'rejected'` (génération seule) veut dire : le modèle a répondu, l'appel a été payé,
   mais son plan ne pouvait pas devenir un fichier. L'écran affiche les deux tels quels. */

export interface AiUsageReport {
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  costCents: number;
  latencyMs: number;
}

export interface AiBudgetReport {
  capCents: number;
  spentCents: number;
  remainingCents: number;
}

export type ActivitySuggestResult =
  | {
      status: 'ok';
      classId: string;
      suggestion: {
        title: string;
        instructionsMd: string;
        appId: string | null;
        /** Le modèle a nommé un outil que cette plateforme n'a pas : le champ reste vide, et
         * l'écran doit le DIRE plutôt que de laisser croire à un oubli. */
        toolDiscarded: boolean;
      };
      usage: AiUsageReport;
      budget: AiBudgetReport;
    }
  | {
      status: 'unavailable';
      classId: string;
      reason: 'not_configured' | 'policy_disabled' | 'budget_exhausted' | 'provider_error';
      message: string;
      budget: AiBudgetReport | null;
    };

/** Un défaut posé dans le fichier généré, et le nombre EXACT de fiches qui le portent. */
export interface GeneratedDefect {
  kind: string;
  requestedPercent: number;
  count: number;
}

export interface GeneratedPreviewRow {
  lastName: string;
  firstName: string;
  email: string;
  phone: string;
  city: string;
  company: string;
  jobTitle: string;
  defect: string | null;
}

export interface GeneratedReport {
  contacts: number;
  accounts: number;
  defects: GeneratedDefect[];
  flawedContacts: number;
  cleanContacts: number;
  preview: GeneratedPreviewRow[];
}

/** Un des fichiers produits par une même demande. `label` est « A », « B »… — ce que l'enseignant
 * lit à l'écran et prononce devant sa classe. */
export interface GeneratedFile {
  label: string;
  fileName: string;
  fileBase64: string;
  report: GeneratedReport;
}

/**
 * **En quoi les fichiers diffèrent, et en quoi ils sont comparables** — calculé par le cœur sur les
 * fichiers réellement produits (`GeneratedVariantComparison`, `@ndrc/core-datasets`), jamais
 * recompté ni affirmé par cet écran. L'enseignant ne peut noter équitablement que s'il voit les
 * deux : `sameDifficulty` dit que le travail demandé est le même, `sharedContacts` dit combien de
 * fiches se retrouvent dans plusieurs fichiers — c'est-à-dire ce qu'un élève pourrait recopier.
 */
export interface GeneratedComparison {
  defects: GeneratedDefect[];
  contactsPerVariant: number;
  sameDifficulty: boolean;
  sharedContacts: number;
}

export type DatasetGenerateResult =
  | {
      status: 'ok';
      classId: string;
      name: string;
      /** Toujours au moins un fichier. Plusieurs quand l'enseignant a demandé la variante par
       * élève — et sans un appel d'IA de plus, voir `datasets.generate` côté cœur. */
      files: GeneratedFile[];
      comparison: GeneratedComparison;
      discardedDefects: string[];
      usage: AiUsageReport;
      budget: AiBudgetReport;
    }
  | {
      status: 'rejected';
      classId: string;
      message: string;
      usage: AiUsageReport;
      budget: AiBudgetReport;
    }
  | {
      status: 'unavailable';
      classId: string;
      reason: 'not_configured' | 'policy_disabled' | 'budget_exhausted' | 'provider_error';
      message: string;
      budget: AiBudgetReport | null;
    };

export interface TeacherCompany {
  id: string;
  name: string;
  sector: string | null;
}

/**
 * Un correspondant PROPOSÉ pour une entreprise que l'enseignant s'apprête à créer
 * (`companies.suggest`). Redéclaré ici comme tout le reste : le bureau ne dépend d'aucun paquet
 * `@ndrc/core-*`.
 *
 * **`brief` est là, et ce n'est pas la fuite que l'ADR-017 interdit.** Ce que l'ADR protège, c'est
 * l'ÉLÈVE : `call.personas` ne rend jamais le brief, et n'a pas changé d'un caractère. Ici le
 * lecteur est l'ADULTE qui décide si ces personnages existent — et c'est justement dans le brief
 * que se juge la qualité d'un correspondant (« celui-ci dit oui à tout »). L'écran le range
 * derrière un dépliant fermé, avec la mention qu'il ne faut pas le projeter : une salle de classe
 * regarde souvent le second écran de l'enseignant.
 */
export interface CompanyPersonaProposal {
  name: string;
  company: string;
  role: string;
  direction: 'inbound' | 'outbound';
  situation: string;
  mood: string;
  voice: 'feminine' | 'masculine';
  brief: string[];
}

export type CompanySuggestResult =
  | {
      status: 'ok';
      classId: string;
      name: string;
      sector: string;
      activity: string;
      personas: CompanyPersonaProposal[];
      discardedPersonas: string[];
      usage: AiUsageReport;
      budget: AiBudgetReport;
    }
  | {
      status: 'unavailable';
      classId: string;
      reason: 'not_configured' | 'policy_disabled' | 'budget_exhausted' | 'provider_error';
      message: string;
      budget: AiBudgetReport | null;
    };

/** Ce que `companies.create` rend une fois l'entreprise écrite. `personaCount` est le nombre
 * RÉELLEMENT rangé — jamais le nombre proposé : l'enseignant en a peut-être retiré. */
export interface CompanyCreateResult {
  companyId: string;
  slug: string;
  name: string;
  sector: string;
  personaCount: number;
}

/** La clé de cache de la liste des entreprises, exportée : créer une entreprise l'allonge, et
 * l'écran qui vient de la créer doit la retrouver dans son propre sélecteur — sans quoi
 * l'enseignant créerait une entreprise qu'il ne pourrait pas choisir. */
export const companiesQueryKey = ['core', 'companies.list'] as const;

/** Les entreprises fictives de l'établissement — celles dont les élèves tiennent le fichier
 * client. `datasets.import` et `datasets.distribute` en exigent une : sans cette lecture, l'écran
 * devrait nommer une entreprise en dur, ce que ce dépôt ne fait nulle part. */
export function useCompanies(): UseQueryResult<{ companies: TeacherCompany[] }, unknown> {
  return useQuery({
    queryKey: companiesQueryKey,
    queryFn: () => callCore<{ companies: TeacherCompany[] }>('companies.list'),
    retry: false,
    staleTime: 60_000,
  });
}

/* --- Les documents de la classe ---------------------------------------------------------------
   `files.list` sert DEUX écrans : « Documents » chez l'élève (§5 du cahier des charges) et la
   liste de l'enseignant, qui y choisit ce qu'il joint à une consigne. Une seule opération, une
   seule clé de cache — deux lectures jumelles auraient divergé au premier changement. */

export const documentsQueryKey = ['core', 'files.list'] as const;

/**
 * Ce que le dépôt accepte, **tel que le serveur le déclare**. Le bureau ne peut pas importer la
 * source de vérité (`@ndrc/core-files`) : la frontière client/serveur interdit tout `@ndrc/core-*`
 * dans le paquet du navigateur. Ces bornes voyagent donc avec la liste des documents — voir
 * `UploadLimits` (`@ndrc/core-domain`, `operations/files.ts`) pour pourquoi elles ne sont pas
 * recopiées ici.
 */
export interface UploadLimits {
  extensions: string[];
  maxBytes: number;
}

export interface ClassDocumentsResult {
  classId: string;
  documents: ClassDocument[];
  limits: UploadLimits;
}

/**
 * Les documents de la classe, les plus récents d'abord.
 *
 * **Pas de `refetchInterval`** : le fil temps réel invalide cette clé quand un signal
 * `core.documents.changed` arrive (`useRealtimeSignals`) — un document déposé pendant la séance
 * apparaît donc chez l'élève sans qu'il recharge, et sans qu'un second sondage double le premier.
 *
 * `staleTime` court (dix secondes) parce que les adresses de fichiers portées par cette réponse
 * sont SIGNÉES et expirent : une réponse conservée trop longtemps finirait par offrir des liens
 * périmés. Dix secondes laissent le cache faire son travail entre deux rendus sans jamais
 * approcher l'échéance (deux heures).
 */
export function useClassDocuments(
  classId: string | undefined,
): UseQueryResult<ClassDocumentsResult, unknown> {
  return useQuery({
    queryKey: [...documentsQueryKey, classId ?? null],
    queryFn: () => callCore<ClassDocumentsResult>('files.list', classId ? { classId } : {}),
    retry: false,
    staleTime: 10_000,
  });
}

export { isUnauthenticated };

/* --- La cloche -------------------------------------------------------------------------------
   `notifications.list` existait et était testée depuis le chantier 5 ; personne ne la lisait. Ce
   qui suit est le seul lecteur du bureau. */

export interface NotificationItem {
  id: string;
  classId: string | null;
  level: string;
  title: string;
  body: string | null;
  readAt: string | null;
  createdAt: string;
}

export const notificationsQueryKey = ['core', 'notifications.list'] as const;

/**
 * Les notifications de celui qui est connecté — jamais celles d'un autre : le cœur filtre sur la
 * session (`where user_id = ctx.actor.userId`), et la politique de la base filtre par-dessus.
 *
 * **Aucun `refetchInterval`, et c'est délibéré.** Le fil temps réel invalide cette clé quand un
 * signal `core.notifications.changed` arrive (`useRealtimeSignals`) : la cloche s'allume en
 * quelques secondes sans qu'un second battement, propre à cet écran, ne double le premier. Un
 * sondage de plus ici ferait trente requêtes de plus par minute et par classe, pour une
 * information que le fil apporte déjà.
 *
 * `limit: 20` : la cloche est une liste courte, pas une messagerie — l'Inbox est une application à
 * part, prévue plus tard.
 */
export function useNotifications(): UseQueryResult<{ notifications: NotificationItem[] }, unknown> {
  return useQuery({
    queryKey: notificationsQueryKey,
    queryFn: () =>
      callCore<{ notifications: NotificationItem[] }>('notifications.list', {
        limit: 20,
        offset: 0,
      }),
    retry: false,
    staleTime: 10_000,
  });
}

/** Les mini-sites sont accessibles sans fichier CRM, y compris depuis le bureau nu. */
export function useInteractiveSessions(classId?: string) {
  return useQuery({
    queryKey: ['core', 'interactive.mine', classId],
    queryFn: () =>
      callCore<{ sessions: import('@ndrc/app-sdk/client').MiniSession[] }>(
        'interactive.mine',
        classId ? { classId } : {},
      ),
    refetchInterval: 5000,
  });
}
